import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

function paymentError(error) {
    try {
        const messages = JSON.parse(error?.response?.data?._server_messages || "[]");
        const first = typeof messages[0] === "string" ? JSON.parse(messages[0]) : messages[0];
        if (first?.message) return String(first.message).replace(/<[^>]*>/g, "");
    } catch (_) { /* Use safe generic fallback. */ }
    return error?.response ? "action" : "connection";
}

export const terminalCall = async (method, args = {}) => {
    const { data } = await axios.post(`method/restaurant_management.terminal_payments.api.${method}`, args, { timeout: 30000 });
    return data.message;
};

export function workstationId() {
    const key = "cayenne.payment.workstation";
    let id = localStorage.getItem(key);
    if (!id) {
        id = window.crypto.randomUUID();
        localStorage.setItem(key, id);
    }
    return id;
}

export default function useTerminalCheckout(order, profile) {
    const [configuration, setConfiguration] = useState(null);
    const [session, setSession] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const locked = useRef(false);
    const generation = useRef(0);
    const polling = useRef(false);
    const [workstation] = useState(workstationId);
    useEffect(() => {
        const version = ++generation.current;
        setSession(null);
        setConfiguration(null);
        setError("");
        if (!order || !profile) return;
        Promise.all([
            terminalCall("configuration", { pos_profile: profile, workstation_id: workstation }),
            terminalCall("resume", { order }),
        ]).then(([config, pending]) => {
            if (version !== generation.current) return;
            setConfiguration(config);
            setSession(pending);
        }).catch(() => {
            if (version === generation.current) setError("connection");
        });
        return () => { generation.current += 1; };
    }, [order, profile, workstation]);

    useEffect(() => {
        if (!order || !profile) return;
        const version = generation.current;
        let running = false;
        const timer = setInterval(async () => {
            if (running || locked.current) return;
            running = true;
            try {
                const config = await terminalCall("configuration", { pos_profile: profile, workstation_id: workstation });
                const pending = !session?.name ? await terminalCall("resume", { order }) : null;
                if (version === generation.current) {
                    setConfiguration(config);
                    if (pending) setSession(pending);
                    setError(previous => previous === "connection" ? "" : previous);
                }
            } catch (_) { /* Status polling preserves any active payment. */ }
            finally { running = false; }
        }, 10000);
        return () => clearInterval(timer);
    }, [order, profile, workstation, session?.name]);

    const refresh = useCallback(async () => {
        if (!session?.name || polling.current || locked.current) return;
        polling.current = true;
        const version = generation.current;
        try {
            const next = await terminalCall("status", { checkout: session.name });
            if (version === generation.current) {
                setSession(next);
                setError("");
            }
        } catch (_) {
            if (version === generation.current) setError("connection");
        } finally { polling.current = false; }
    }, [session?.name]);
    useEffect(() => {
        if (!session?.name || ["Completed", "Cancelled"].includes(session.status)) return;
        const timer = setInterval(refresh, 2000);
        return () => clearInterval(timer);
    }, [session, refresh, error]);

    const action = useCallback(async (method, extra = {}) => {
        if (locked.current) return;
        locked.current = true;
        const version = generation.current;
        setBusy(true);
        setError("");
        try {
            const next = await terminalCall(method, { checkout: session?.name, ...extra });
            if (version !== generation.current) return;
            setSession(next);
            return next;
        } catch (error) {
            if (version !== generation.current) return;
            setError(paymentError(error));
        } finally {
            locked.current = false;
            setBusy(false);
        }
    }, [session?.name]);

    const begin = useCallback(async (payload, items, masterPassword) => {
        if (locked.current) return;
        locked.current = true;
        const version = generation.current;
        setBusy(true);
        setError("");
        try {
            const prepared = await terminalCall("prepare", {
                order, workstation_id: workstation, payload,
                items, master_password: masterPassword,
            });
            if (version !== generation.current) return;
            setSession(prepared); // Keep recovery visible even if start fails.
            if (prepared.status === "Prepared") {
                const next = await terminalCall("start", { checkout: prepared.name });
                if (version === generation.current) setSession(next);
            }
        } catch (error) {
            if (version !== generation.current) return;
            setError(paymentError(error));
            // Preparation may have committed before the HTTP response was lost.
            try {
                const pending = await terminalCall("resume", { order });
                if (version === generation.current) setSession(pending);
            } catch (_) { /* retain error */ }
        } finally {
            locked.current = false;
            setBusy(false);
        }
    }, [order, workstation]);

    return { configuration, session, busy, error, workstation, begin, action, refresh };
}
